import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Images, ArrowUpRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Lightbox from "@/components/site/Lightbox";
import MobileCarousel from "@/components/site/MobileCarousel";
import {
  ALBUM_FILTERS,
  CATEGORY_LABELS,
} from "@/data/gallery";
import { useGalleryAlbums } from "@/hooks/useGalleryAlbums";
import { useSiteContent } from "@/hooks/useSiteContent";
import { Heading } from "@/data/siteContent";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const Hero = ({ s }) => (
  <section data-testid="gallery-hero" className="relative -mt-[80px] flex min-h-[42vh] items-center justify-center overflow-hidden lg:-mt-[78px]">
    <img
      src={s.gallery_hero_image}
      alt="Live event by Wonder Events"
      className="absolute inset-0 h-full w-full object-cover"
      loading="eager"
    />
    <div className="absolute inset-0 bg-[#1F1F1F]/70" />
    <div className="relative z-10 mx-auto max-w-4xl px-5 py-20 text-center">
      <motion.span
        {...fadeUp()}
        data-testid="gallery-hero-eyebrow"
        className="inline-block rounded-full border border-white/30 bg-white/10 px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] text-white backdrop-blur"
      >
        {s.gallery_hero_eyebrow}
      </motion.span>
      <motion.h1
        {...fadeUp(0.12)}
        className="mt-5 text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl"
      >
        <Heading value={s.gallery_hero_heading} />
      </motion.h1>
      <motion.p {...fadeUp(0.22)} className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#E5E5E5] sm:text-lg">
        {s.gallery_hero_text}
      </motion.p>
    </div>
  </section>
);

const AlbumCard = ({ album, index, onOpen }) => (
  <motion.button
    layout
    data-testid={`album-card-${album.id}`}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    transition={{ duration: 0.45, delay: Math.min(index * 0.06, 0.3) }}
    onClick={onOpen}
    className="group relative block w-full overflow-hidden rounded-2xl bg-white text-left shadow-[0_6px_24px_rgba(0,0,0,0.07)] ring-1 ring-[#F0F0F0] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_45px_rgba(198,40,40,0.14)]"
    aria-label={`View ${album.title} gallery`}
  >
    <div className="relative h-64 overflow-hidden sm:h-72">
      <img
        src={album.cover}
        alt={album.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F]/75 via-[#1F1F1F]/10 to-transparent" />
      <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#1F1F1F] backdrop-blur">
        <Images size={13} className="text-[#C62828]" />
        {album.photos.length} {album.photos.length === 1 ? "Photo" : "Photos"}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E05252]">
          {CATEGORY_LABELS[album.category]}
        </span>
        <h3 className="mt-1.5 text-lg font-extrabold leading-snug text-white">{album.title}</h3>
      </div>
    </div>
    <div className="flex items-center justify-between px-5 py-4">
      <span className="text-xs font-bold uppercase tracking-wider text-[#666666] transition-colors duration-300 group-hover:text-[#C62828]">
        View Gallery
      </span>
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] text-[#666666] transition-all duration-300 group-hover:border-[#C62828] group-hover:bg-[#C62828] group-hover:text-white">
        <ArrowUpRight size={16} />
      </span>
    </div>
  </motion.button>
);

const Albums = ({ s }) => {
  const [filter, setFilter] = useState("all");
  const [openAlbum, setOpenAlbum] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const { albums } = useGalleryAlbums();

  const filtered =
    filter === "all" ? albums : albums.filter((a) => a.category === filter);

  const album = openAlbum !== null ? filtered.find((a) => a.id === openAlbum) : null;

  return (
    <section data-testid="albums-section" className="bg-[#F8F8F8] py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div {...fadeUp()} className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-[#1F1F1F] sm:text-4xl">
            {s.albums_heading}
          </h2>
          <span className="mx-auto mt-4 block h-[4px] w-16 rounded-full bg-[#C62828]" />
          <p className="mt-4 text-base text-[#666666]">
            {s.albums_text}
          </p>
        </motion.div>

        <div data-testid="album-filters" className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
          {ALBUM_FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`album-filter-${f.id}`}
              onClick={() => { setFilter(f.id); setOpenAlbum(null); }}
              className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
                filter === f.id
                  ? "bg-[#C62828] text-white shadow-[0_6px_16px_rgba(198,40,40,0.3)]"
                  : "border border-[#E0E0E0] bg-white text-[#666666] hover:border-[#C62828] hover:text-[#C62828]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout data-testid="albums-grid" className="mt-10 hidden gap-7 sm:grid sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((a, i) => (
              <AlbumCard
                key={a.id}
                album={a}
                index={i}
                onOpen={() => { setOpenAlbum(a.id); setPhotoIndex(0); }}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="mt-10">
          <MobileCarousel testid="albums-carousel">
            {filtered.map((a, i) => (
              <AlbumCard
                key={a.id}
                album={a}
                index={i}
                onOpen={() => { setOpenAlbum(a.id); setPhotoIndex(0); }}
              />
            ))}
          </MobileCarousel>
        </div>
      </div>

      <Lightbox
        images={album ? album.photos : []}
        index={album ? photoIndex : null}
        categoryLabel={album ? album.title : ""}
        onClose={() => setOpenAlbum(null)}
        onPrev={() => setPhotoIndex((i) => (i - 1 + album.photos.length) % album.photos.length)}
        onNext={() => setPhotoIndex((i) => (i + 1) % album.photos.length)}
      />
    </section>
  );
};

const FinalCTA = ({ s }) => (
  <section data-testid="gallery-cta-section" className="bg-[#C62828] py-20 lg:py-24">
    <div className="mx-auto max-w-4xl px-5 text-center">
      <motion.div {...fadeUp()}>
        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          {s.gallery_cta_heading}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-white/85 sm:text-lg">
          {s.gallery_cta_text}
        </p>
        <a
          data-testid="gallery-final-cta-button"
          href={s.contact_url}
          className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#C62828] shadow-[0_14px_35px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#1F1F1F] hover:text-white"
        >
          {s.gallery_cta_button}
          <ArrowRight size={17} strokeWidth={2.5} />
        </a>
      </motion.div>
    </div>
  </section>
);

const Gallery = () => {
  const s = useSiteContent();

  return (
    <div data-testid="gallery-page" className="bg-white">
      <Header />
      <main>
        <Hero s={s} />
        <Albums s={s} />
        <FinalCTA s={s} />
      </main>
      <Footer />
    </div>
  );
};

export default Gallery;
